/**
 * WHERE A ROOM IS LOOKING — §11's follow-the-leader, as a message rather than a screen share.
 *
 * Every client already holds the corpora: that is what the content plane IS (static, immutable,
 * cached, downloadable). So there is nothing to transmit but **what to show** — a pointer, a few
 * dozen bytes, into text the reader already has. What falls out of that is better than the thing it
 * replaces rather than merely cheaper: each reader sees the passage in their own edition and their
 * own language, at their own size, on a phone if that is what they have. A screen share can only
 * ever show one edition, in one language, at one font size, and this product's whole subject is the
 * same passage across traditions.
 *
 * THE MESSAGE IS THE APP'S OWN ADDRESS, not a second format invented beside it. §11 says so and the
 * reason is that the code which applies an arriving permalink is the code which applies a leader's
 * move: `web/src/nav/permalink.ts` already names these parts, already restores them, and is already
 * tested. A second shape here would be a second answer to "what is a view", and the two would
 * differ in one of them — the scar this repository carries beside `mustBeAsked` and
 * `licence-policy.ts`.
 *
 * `signin` IS DELIBERATELY NOT A PART, though the app's own state carries it. It is a message about
 * one reader's arrival — a sign-in that did not finish — and relaying it would put a sentence about
 * somebody else's Google account in front of three hundred people. A view is what a room can share;
 * a message to one reader is not.
 *
 * WHAT IS DELIBERATELY NOT HERE, the same two things `chat.ts` leaves out:
 *
 *   - **The transport.** In-room following rides the LiveKit data channel (D25) because the join
 *     token already grants it, and because nothing is stored: the social plane holds node ids and
 *     never scripture (D10), and a mark is a range inside a passage. Relaying keeps that seam intact
 *     by construction rather than by discipline — there is no collection to leak into.
 *   - **Who may drive.** Moving three hundred readers to a different verse is a griefing vector
 *     before it is a feature, so driving is a capability and not a right (§11): it belongs to hosts
 *     and speakers, which is what `capabilitiesFor` already decides. A packet cannot carry that —
 *     anyone can write one — so it is the RECEIVER that checks the sender against the roster it
 *     already has. Nothing in this file grants anything.
 */

/**
 * The longest any one part may be, and the longest a whole packet may be.
 *
 * A part is a node id, a citation, a comparison key or a Hall's address — the same strings the
 * address bar carries, all short. 200 is far above every one of them and far below anything that
 * could be used to push work through a room, and the packet cap is what a reader who forges one
 * cannot exceed: `readFollow` refuses rather than parses.
 */
export const MAX_FOLLOW_PART = 200;
export const MAX_FOLLOW_PACKET = 1024;

/** The kind, on the wire, beside `chat` and `changed`. */
export const FOLLOW = 'follow';

/**
 * What a room is looking at — the app's own view state, minus the one part that is not a view.
 *
 * Every part is optional and an empty view is a real destination: a leader closing the reader is
 * saying *look at nothing in particular*, and a follower who cannot be told that would be left
 * reading a passage the room has moved off.
 */
export interface FollowView {
  /** The open figure, by node id. */
  node?: string;
  /** The reader, by the citation of the verse it centres on. */
  passage?: string;
  /** A side-by-side comparison, by its key. */
  compare?: string;
  /** A full panel: `sources` or `disputed`. */
  panel?: string;
  /** The discussion drawer, by its scope key. */
  discuss?: string;
  /** A leader's Hall, by slug. */
  hall?: string;
}

/**
 * The parts, named once. `readFollow` keeps these and drops everything else, so a packet carrying a
 * field this product does not have is not a way to reach the code that applies one.
 */
export const FOLLOW_PARTS = ['node', 'passage', 'compare', 'panel', 'discuss', 'hall'] as const;

/**
 * ONE STRING FOR ONE VIEW, so two of them can be compared without asking how they were built.
 *
 * The sending half needs to tell three things apart that all arrive as *"the view changed"*: a
 * move this reader made, a move that was applied because somebody else made it, and no change at
 * all. Comparing objects means comparing key ORDER, and the two objects come from different
 * places — the app builds one when it reflects state into the address bar, `readFollow` builds
 * the other from the wire — so `JSON.stringify` would say two identical views differ.
 *
 * The parts in their declared order, absent ones as empty. A key is for comparing and never for
 * sending: it is not the packet, and nothing parses it back.
 */
export function viewKey(view: FollowView): string {
  return FOLLOW_PARTS.map((part) => `${part}=${view[part] ?? ''}`).join('|');
}

/** A leader's move, ready to publish. `at` is the SENDER's clock and is never read as a time. */
export function followPacket(view: FollowView, at: number): Uint8Array<ArrayBuffer> {
  const kept: Record<string, string> = {};
  for (const part of FOLLOW_PARTS) {
    const value = view[part];
    if (typeof value === 'string' && value.trim() !== '') kept[part] = value.trim().slice(0, MAX_FOLLOW_PART);
  }
  return new TextEncoder().encode(JSON.stringify({ kind: FOLLOW, at, view: kept }));
}

/**
 * A move somebody else made, or null.
 *
 * Null for anything that is not this product's message: another kind, a packet too big to be one of
 * ours, bytes that are not JSON. A packet that IS ours but carries junk in a part loses that part
 * rather than the whole move — a leader whose compare key was mangled in flight should still take
 * the room to the passage.
 */
export function readFollow(bytes: Uint8Array): FollowView | null {
  if (bytes.byteLength > MAX_FOLLOW_PACKET) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) return null;

  /*
   * ASKED, NOT ASSERTED — the same reading the other two places in this plane use.
   *
   * This narrowed with `parsed as Record<string, unknown>`, which is sound after the guard above and
   * is still the wrong idiom here, because the plane has already argued the point twice about this
   * exact job. `readPayload` in the web connector: *"Narrowed by `in` rather than asserted with a
   * cast: 'does this object have a field called kind' is a question TypeScript can answer, and
   * answering it is the whole job here."* And `SocialClient.refused` reads a field off an unknown
   * body with `Reflect.get` for the same reason. Three readers of untrusted JSON, one idiom.
   */
  const field = (of: object, name: string): unknown => (name in of ? Reflect.get(of, name) : undefined);

  if (field(parsed, 'kind') !== FOLLOW) return null;

  const carried = field(parsed, 'view');
  const view = typeof carried === 'object' && carried !== null ? carried : {};

  const move: FollowView = {};
  for (const part of FOLLOW_PARTS) {
    const value = field(view, part);
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed === '' || trimmed.length > MAX_FOLLOW_PART) continue;
    move[part] = trimmed;
  }
  return move;
}
